"use client";

import { useState } from "react";
import { useAction } from "convex/react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { toast } from "sonner";
import { api } from "@convex/_generated/api";
import type { ListingQuoteArgs } from "@convex/quotes/mutations/listingQuoteArgs";
import type { SignInLink } from "@convex/constants/signInLinkFlows";
import { signInWithEmailCode } from "@/components/auth/sign-in-actions";
import { track } from "@/lib/analytics/tracker";
import { reportSignInConversion } from "@/lib/auth/sign-in/conversion";
import { INVALID_CODE_MESSAGE, type SignInErrorReason } from "@/lib/auth/sign-in/result";
import { signInLinkHere } from "@/lib/auth/emailed-sign-in";
import {
  sendFailureReason,
  trackAuthCompleted,
  trackAuthFailure,
  type AuthAttempt,
} from "@/lib/auth/sign-in/auth-tracking";
import { navigateToDocument } from "@/lib/document-navigation";
import { describeError } from "@/lib/handle-error";
import {
  useSignInCodeDelivery,
  type SentSignInCode,
} from "@/components/auth/use-sign-in-code-delivery";
import type { GuestContactState } from "./listing-quote-request-state";

const LISTING_QUOTE_ATTEMPT: AuthAttempt = { entryPoint: "listing_quote", method: "magic_code" };

const REQUEST_FAILED_MESSAGE =
  "You're signed in, but the request could not be sent. Try again.";

export interface GuestQuoteSubmission {
  leadId: string;
  leadNumber: string;
  alreadyRequested: boolean;
}

type VerifyOutcome =
  | { kind: "submitted"; submission: GuestQuoteSubmission }
  | { kind: "request_failed"; error: string }
  | { kind: "code_rejected"; error: string; reason: SignInErrorReason }
  | { kind: "hosted" };

async function verifyAndSubmit(input: {
  email: string;
  code: string;
  contact: GuestContactState;
  request: ListingQuoteArgs;
}): Promise<VerifyOutcome> {
  const result = await signInWithEmailCode({
    email: input.email,
    code: input.code,
    listingRequest: {
      profile: {
        firstName: input.contact.firstName.trim(),
        lastName: input.contact.lastName.trim(),
        phone: input.contact.phone,
      },
      request: input.request,
    },
  }).catch((cause: unknown) => {
    trackAuthFailure(LISTING_QUOTE_ATTEMPT, "verify", "exception");
    throw cause;
  });
  if (result.status === "needs_hosted" || result.status === "needs_code") return { kind: "hosted" };
  if (result.status !== "signed_in") {
    return { kind: "code_rejected", error: result.message, reason: result.reason };
  }
  track("auth.code_verified", { entry_point: "listing_quote", is_new: result.isNew });
  trackAuthCompleted(LISTING_QUOTE_ATTEMPT, result.isNew);
  reportSignInConversion(result.isNew, "magic_code");
  const request = result.listingRequest ?? { error: REQUEST_FAILED_MESSAGE };
  return "error" in request
    ? { kind: "request_failed", error: request.error }
    : { kind: "submitted", submission: request };
}

type SendCode = (args: { email: string; link: SignInLink }) => Promise<SentSignInCode>;

type OnSent = (sent: SentSignInCode | null) => void;

async function sendListingQuoteCode(sendCode: SendCode, email: string, onSent: OnSent): Promise<string> {
  onSent(null);
  try {
    const sent = await sendCode({ email, link: signInLinkHere("listing_quote") });
    onSent(sent);
    return sent.email;
  } catch (cause) {
    trackAuthFailure(LISTING_QUOTE_ATTEMPT, "send", sendFailureReason(cause));
    throw cause;
  }
}

async function resendCode(
  email: string | null,
  sendCode: SendCode,
  run: (work: () => Promise<void>, fallback: string) => Promise<void>,
  onSent: OnSent,
): Promise<boolean> {
  if (!email) return false;
  let sent = false;
  await run(async () => {
    await sendListingQuoteCode(sendCode, email, onSent);
    sent = true;
  }, "We couldn't resend the code.");
  return sent;
}

function trackUnsubmitted(outcome: Exclude<VerifyOutcome, { kind: "submitted" }>): void {
  if (outcome.kind === "hosted") trackAuthFailure(LISTING_QUOTE_ATTEMPT, "verify", "needs_hosted");
  else if (outcome.kind === "code_rejected") trackAuthFailure(LISTING_QUOTE_ATTEMPT, "verify", outcome.reason);
  else trackAuthFailure(LISTING_QUOTE_ATTEMPT, "listing_request", "request_failed");
}

async function settleSession(
  refreshAuth: () => Promise<{ error?: string } | void>,
): Promise<void> {
  const refreshed = await refreshAuth().catch(() => ({ error: "refresh_failed" }));
  if (!refreshed?.error) return;
  window.location.reload();
}

function handleUnsubmitted(
  outcome: Exclude<VerifyOutcome, { kind: "submitted" }>,
  controls: { resetCode: () => void; setError: (message: string) => void },
): void {
  trackUnsubmitted(outcome);
  if (outcome.kind === "hosted") {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    navigateToDocument(`/sign-in?${new URLSearchParams({ returnTo }).toString()}`);
    return;
  }
  if (outcome.kind === "request_failed") controls.resetCode();
  controls.setError(outcome.error);
}

async function finishSubmission(
  outcome: GuestQuoteSubmission,
  handlers: {
    onSubmitted: (submission: GuestQuoteSubmission) => void;
    refreshAuth: () => Promise<{ error?: string } | void>;
  },
): Promise<void> {
  if (outcome.alreadyRequested) {
    toast.info(`You already asked about this listing (${outcome.leadNumber})`);
    navigateToDocument(`/dashboard/orders/${outcome.leadId}`);
    return;
  }
  toast.success(`Request sent (${outcome.leadNumber})`);
  handlers.onSubmitted(outcome);
  await settleSession(handlers.refreshAuth);
}

function useBusyRunner() {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (work: () => Promise<void>, fallback: string) => {
    setIsBusy(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      setError(describeError(cause, fallback));
    } finally {
      setIsBusy(false);
    }
  };
  return { isBusy, error, setError, run };
}

interface CodeStepPersistence {
  restoredEmail: string | null;
  onCodeSent: (email: string) => void;
  onCodeStepLeft: () => void;
}

function useCodeStep(persistence: CodeStepPersistence) {
  const [codeSentTo, setCodeSentToState] = useState<string | null>(persistence.restoredEmail);
  const delivery = useSignInCodeDelivery(LISTING_QUOTE_ATTEMPT);
  const setCodeSentTo = (email: string | null) => {
    setCodeSentToState(email);
    if (email === null) {
      delivery.setSent(null);
      persistence.onCodeStepLeft();
    } else persistence.onCodeSent(email);
  };
  return [codeSentTo, setCodeSentTo, delivery] as const;
}

export function useGuestQuoteSignIn(
  input: CodeStepPersistence & {
    onSubmitted: (submission: GuestQuoteSubmission) => void;
  },
) {
  const sendCode = useAction(api.auth.magicCode.sendSignInCode);
  const { refreshAuth } = useAuth();
  const [codeSentTo, setCodeSentTo, delivery] = useCodeStep(input);
  const runner = useBusyRunner();

  const start = (contact: GuestContactState) =>
    runner.run(async () => {
      track("auth.started", { entry_point: "listing_quote", method: "magic_code" });
      setCodeSentTo(await sendListingQuoteCode(sendCode, contact.email.trim(), delivery.setSent));
    }, "We couldn't send a code. Please try again.");

  const resend = () => resendCode(codeSentTo, sendCode, runner.run, delivery.setSent);

  const verify = (code: string, contact: GuestContactState, request: ListingQuoteArgs) =>
    codeSentTo
      ? runner.run(async () => {
          const outcome = await verifyAndSubmit({ email: codeSentTo, code, contact, request });
          if (outcome.kind !== "submitted") {
            handleUnsubmitted(outcome, { resetCode: () => setCodeSentTo(null), setError: runner.setError });
            return;
          }
          input.onCodeStepLeft();
          await finishSubmission(outcome.submission, {
            onSubmitted: input.onSubmitted,
            refreshAuth,
          });
        }, INVALID_CODE_MESSAGE)
      : Promise.resolve();

  return {
    codeSentTo,
    isBusy: runner.isBusy,
    error: runner.error ?? delivery.error,
    start,
    resend,
    verify,
    clearError: () => runner.setError(null),
    changeEmail: () => {
      setCodeSentTo(null);
      runner.setError(null);
    },
  };
}
